'use client';

import type { ReactNode } from 'react';
import type { ToolUIPart } from 'ai';

import { MessageResponse } from '../ui/Message/Message';
import { Reasoning, ReasoningTrigger, ReasoningContent } from '../ui/Reasoning/Reasoning';
import { ToolPart } from '../ui/Tool/ToolPart';

import type { MessagePart } from './types';
import { isReasoningPart, isTextPart, isStaticToolPart } from './types';
import { getToolNameFromToolPart } from './toolParts';

export type ToolPartRendererArgs = {
  toolName: string;
  toolCallId: string;
  state: ToolUIPart['state'];
  input: unknown;
  output?: unknown;
  errorText?: string;
};

export type ToolPartRenderer = (args: ToolPartRendererArgs) => ReactNode;

/** Render a single non-reasoning message part to a ReactNode, or null if unhandled. */
function renderMessagePart(
  part: MessagePart,
  messageId: string,
  partIndex: number,
  options?: RenderMessagePartsOptions
): ReactNode {
  const key = `${messageId}-${partIndex}`;

  if (part.type === 'step-start') {
    return null;
  }

  if (isTextPart(part)) {
    return <MessageResponse key={`${key}-text`}>{part.text}</MessageResponse>;
  }

  if (isStaticToolPart(part)) {
    const toolPart = part as unknown as ToolUIPart;
    const toolName = getToolNameFromToolPart(toolPart);
    const custom = options?.toolPartRenderers?.[toolName];
    const output = toolPart.state === 'output-available' ? toolPart.output : undefined;
    const errorText = toolPart.state === 'output-error' ? toolPart.errorText : undefined;

    return (
      <div key={`${key}-tool`}>
        <ToolPart
          toolName={toolName}
          toolCallId={toolPart.toolCallId}
          state={toolPart.state}
          input={toolPart.input}
          output={output}
          errorText={errorText}
          customRenderer={custom}
        />
      </div>
    );
  }

  return null;
}

/** Render all parts of a message to ReactNode array. */
export function renderMessageParts(parts: MessagePart[], messageId: string): ReactNode[] {
  return renderMessagePartsWithOptions(parts, messageId);
}

export type RenderMessagePartsOptions = {
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
};

function isPartStreaming(part: MessagePart): boolean {
  return 'state' in part && typeof part.state === 'string'
    ? part.state.includes('streaming')
    : false;
}

/** Render all parts of a message to ReactNode array.
 *
 * All reasoning parts in a single message turn are collapsed into one
 * `<Reasoning>` block (emitted at the position of the first reasoning part) so
 * the chain of thought reads as one continuous stream rather than separate
 * collapsibles interleaved with tool calls.
 */
export function renderMessagePartsWithOptions(
  parts: MessagePart[],
  messageId: string,
  options?: RenderMessagePartsOptions
): ReactNode[] {
  let firstReasoningIndex = -1;
  const reasoningTexts: string[] = [];
  let isAnyReasoningStreaming = false;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    if (!isReasoningPart(part)) continue;
    if (firstReasoningIndex === -1) firstReasoningIndex = i;
    reasoningTexts.push(part.text);
    if (isPartStreaming(part)) isAnyReasoningStreaming = true;
  }
  const combinedReasoningText = reasoningTexts
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const nodes: ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    if (isReasoningPart(part)) {
      if (i !== firstReasoningIndex) continue;
      nodes.push(
        <Reasoning key={`${messageId}-reasoning`} isStreaming={isAnyReasoningStreaming}>
          <ReasoningTrigger />
          <ReasoningContent>{combinedReasoningText}</ReasoningContent>
        </Reasoning>
      );
      continue;
    }
    const node = renderMessagePart(part, messageId, i, options);
    if (node !== null) {
      nodes.push(node);
    }
  }
  return nodes;
}
